import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useClipEditor } from '../hooks/useClipEditor';
import { useVideoStore } from '../store/videoStore';

const ClipItem = ({ clip, trackId, trackColor, watermark }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(clip.title);
  const [editDescription, setEditDescription] = useState(clip.description);
  const [showTooltip, setShowTooltip] = useState(false);
  
  const clipRef = useRef(null);
  const {
    removeClip,
    updateClipInfo,
    duplicateClip,
    splitClip,
    trimClipDuration,
    startDragClip,
    endDragClip
  } = useClipEditor();
  
  const { seekTo, formatTime } = useVideoStore();

  // 格式化时间显示
  const formatClipTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const milliseconds = Math.floor((time % 1) * 1000);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  };

  // 双击预览片段
  const handleDoubleClick = () => {
    // 创建片段预览窗口
    const previewWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=no,resizable=yes');
    if (!previewWindow) {
      alert('无法打开预览窗口，请检查浏览器弹窗设置');
      return;
    }
    
    // 获取原始视频文件
    const videoStore = useVideoStore.getState();
    const videoFile = videoStore.videoFile;
    
    if (!videoFile) {
      alert('找不到原始视频文件，请重新加载视频');
      previewWindow.close();
      return;
    }
    
    // 构建预览页面HTML
    const previewHTML = `
      <!DOCTYPE html>
      <html lang="zh-CN">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>片段预览: ${clip.title}</title>
        <style>
          body {
            margin: 0;
            padding: 20px;
            background: #1a1a1a;
            color: white;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            display: flex;
            flex-direction: column;
            height: 100vh;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 1px solid #333;
          }
          .clip-title {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 10px;
            color: ${trackColor};
          }
          .clip-info {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 10px;
            font-size: 14px;
            color: #ccc;
          }
          .video-container {
            flex: 1;
            position: relative;
            background: #000;
            border-radius: 8px;
            overflow: hidden;
            margin-bottom: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .video-element {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
          .watermark {
            position: absolute;
            bottom: 15px;
            left: 15px;
            color: rgba(255, 255, 255, 0.8);
            font-size: 16px;
            font-weight: bold;
            text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
            z-index: 10;
            background: rgba(0, 0, 0, 0.3);
            padding: 5px 10px;
            border-radius: 4px;
          }
          .controls {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 15px;
            margin-bottom: 20px;
            padding: 15px;
            background: #2d2d2d;
            border-radius: 8px;
          }
          .btn {
            background: #007AFF;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
          }
          .btn:hover {
            background: #0056CC;
          }
          .btn:disabled {
            background: #666;
            cursor: not-allowed;
          }
          .btn-primary {
            background: ${trackColor};
          }
          .btn-primary:hover {
            filter: brightness(0.9);
          }
          .time-display {
            font-family: monospace;
            font-size: 16px;
            color: #fff;
            background: #333;
            padding: 8px 12px;
            border-radius: 4px;
          }
          .progress-container {
            margin-top: 10px;
          }
          .progress-bar {
            width: 100%;
            height: 6px;
            background: #333;
            border-radius: 3px;
            cursor: pointer;
            position: relative;
          }
          .progress-fill {
            height: 100%;
            background: ${trackColor};
            border-radius: 3px;
            transition: width 0.1s ease;
          }
          .progress-thumb {
            position: absolute;
            top: -4px;
            width: 14px;
            height: 14px;
            background: ${trackColor};
            border-radius: 50%;
            cursor: pointer;
            transform: translateX(-50%);
          }
          .description {
            background: #2d2d2d;
            padding: 15px;
            border-radius: 8px;
            margin-top: 10px;
          }
          .description h4 {
            margin: 0 0 10px 0;
            color: #fff;
          }
          .description p {
            margin: 0;
            color: #ccc;
            line-height: 1.5;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="clip-title">${clip.title}</div>
          <div class="clip-info">
            <div>开始时间: ${formatClipTime(clip.startTime)}</div>
            <div>结束时间: ${formatClipTime(clip.endTime)}</div>
            <div>片段时长: ${formatClipTime(clip.duration)}</div>
            <div>类型: ${clip.type === 'offense' ? '进攻' : '防守'}</div>
          </div>
        </div>
        
        <div class="video-container">
          <video id="previewVideo" class="video-element" controls>
            <source src="${videoFile}" type="video/mp4">
            您的浏览器不支持视频播放
          </video>
          ${watermark ? `<div class="watermark">${watermark}</div>` : ''}
        </div>
        
        <div class="controls">
          <button class="btn" onclick="seekToStart()">跳到开始</button>
          <button class="btn btn-primary" onclick="playClip()" id="playBtn">播放片段</button>
          <button class="btn" onclick="toggleLoop()" id="loopBtn">循环播放</button>
          <div class="time-display" id="timeDisplay">00:00 / ${formatClipTime(clip.duration)}</div>
        </div>
        
        <div class="progress-container">
          <div class="progress-bar" onclick="handleSeek(event)">
            <div class="progress-fill" id="progressFill"></div>
            <div class="progress-thumb" id="progressThumb"></div>
          </div>
        </div>
        
        ${clip.description ? `
          <div class="description">
            <h4>片段描述</h4>
            <p>${clip.description}</p>
          </div>
        ` : ''}
        
        <script>
          const video = document.getElementById('previewVideo');
          const playBtn = document.getElementById('playBtn');
          const loopBtn = document.getElementById('loopBtn');
          const timeDisplay = document.getElementById('timeDisplay');
          const progressFill = document.getElementById('progressFill');
          const progressThumb = document.getElementById('progressThumb');
          
          const clipStart = ${clip.startTime};
          const clipEnd = ${clip.endTime};
          const clipDuration = ${clip.duration};
          let isLooping = false;
          let isPlaying = false;
          
          function formatTime(time) {
            const minutes = Math.floor(time / 60);
            const seconds = Math.floor(time % 60);
            return minutes.toString().padStart(2, '0') + ':' + 
                   seconds.toString().padStart(2, '0');
          }
          
          function updateProgress() {
            if (!video.duration) return;
            
            const currentClipTime = Math.max(0, video.currentTime - clipStart);
            const progress = Math.min(currentClipTime / clipDuration, 1);
            
            progressFill.style.width = (progress * 100) + '%';
            progressThumb.style.left = (progress * 100) + '%';
            
            timeDisplay.textContent = formatTime(currentClipTime) + ' / ' + formatTime(clipDuration);
          }
          
          function seekToStart() {
            video.currentTime = clipStart;
            updateProgress();
          }
          
          function playClip() {
            if (isPlaying) {
              video.pause();
              isPlaying = false;
              playBtn.textContent = '播放片段';
            } else {
              if (video.currentTime < clipStart || video.currentTime >= clipEnd) {
                video.currentTime = clipStart;
              }
              video.play();
              isPlaying = true;
              playBtn.textContent = '暂停';
            }
          }
          
          function toggleLoop() {
            isLooping = !isLooping;
            loopBtn.textContent = isLooping ? '取消循环' : '循环播放';
            loopBtn.style.background = isLooping ? '#FF6B35' : '#007AFF';
          }
          
          function handleSeek(event) {
            const rect = event.currentTarget.getBoundingClientRect();
            const percent = (event.clientX - rect.left) / rect.width;
            const newTime = clipStart + (percent * clipDuration);
            video.currentTime = Math.max(clipStart, Math.min(newTime, clipEnd));
            updateProgress();
          }
          
          // 视频事件监听
          video.addEventListener('loadedmetadata', function() {
            seekToStart();
            console.log('视频加载完成，跳转到片段开始位置');
          });
          
          video.addEventListener('timeupdate', function() {
            updateProgress();
            
            // 检查是否超出片段范围
            if (video.currentTime >= clipEnd) {
              if (isLooping) {
                video.currentTime = clipStart;
              } else {
                video.pause();
                isPlaying = false;
                playBtn.textContent = '播放片段';
              }
            }
          });
          
          video.addEventListener('play', function() {
            isPlaying = true;
            playBtn.textContent = '暂停';
          });
          
          video.addEventListener('pause', function() {
            isPlaying = false;
            playBtn.textContent = '播放片段';
          });
          
          video.addEventListener('error', function(e) {
            console.error('视频加载错误:', e);
            alert('视频加载失败，请检查文件路径');
          });
          
          // 键盘快捷键
          document.addEventListener('keydown', function(e) {
            switch(e.code) {
              case 'Space':
                e.preventDefault();
                playClip();
                break;
              case 'KeyL':
                e.preventDefault();
                toggleLoop();
                break;
              case 'KeyR':
                e.preventDefault();
                seekToStart();
                break;
              case 'ArrowLeft':
                e.preventDefault();
                video.currentTime = Math.max(clipStart, video.currentTime - 5);
                break;
              case 'ArrowRight':
                e.preventDefault();
                video.currentTime = Math.min(clipEnd, video.currentTime + 5);
                break;
            }
          });
          
          // 初始化
          console.log('片段预览初始化完成');
          console.log('片段范围:', clipStart, '-', clipEnd, '秒');
        </script>
      </body>
      </html>
    `;
    
    previewWindow.document.write(previewHTML);
    previewWindow.document.close();
    
    console.log('片段预览窗口已打开:', clip.title);
  };

  // 保存编辑
  const handleSaveEdit = () => {
    if (editTitle.trim() !== clip.title || editDescription !== clip.description) {
      updateClipInfo(trackId, clip.id, {
        title: editTitle.trim(),
        description: editDescription
      });
    }
    setIsEditing(false);
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setEditTitle(clip.title);
    setEditDescription(clip.description);
    setIsEditing(false);
  };

  // 删除片段
  const handleDelete = () => {
    if (window.confirm('确定要删除这个片段吗？')) {
      removeClip(trackId, clip.id);
    }
  };

  // 复制片段
  const handleDuplicate = () => {
    duplicateClip(trackId, clip.id);
  };

  // 分割片段
  const handleSplit = () => {
    const splitTime = prompt('请输入分割时间点（秒）:', 
      ((clip.startTime + clip.endTime) / 2).toFixed(3)
    );
    
    if (splitTime) {
      const time = parseFloat(splitTime);
      if (time > clip.startTime && time < clip.endTime) {
        splitClip(trackId, clip.id, time);
      } else {
        alert('分割时间点必须在片段范围内');
      }
    }
  };

  // 修剪开始时间
  const handleTrimStart = (e) => {
    e.stopPropagation();
    startDragClip(clip.id, trackId, 'trim-start', e.clientX);
  };

  // 修剪结束时间
  const handleTrimEnd = (e) => {
    e.stopPropagation();
    startDragClip(clip.id, trackId, 'trim-end', e.clientX);
  };

  // 开始拖拽移动
  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({
      clipId: clip.id,
      trackId: trackId,
      clipData: clip
    }));
    e.dataTransfer.effectAllowed = 'move';
    startDragClip(clip.id, trackId, 'move', { x: e.clientX, y: e.clientY });
  };

  // 右键菜单
  const handleContextMenu = (e) => {
    e.preventDefault();
    
    const menu = [
      { label: '播放', action: () => seekTo(clip.startTime) },
      { label: '编辑', action: () => setIsEditing(true) },
      { label: '复制', action: handleDuplicate },
      { label: '分割', action: handleSplit },
      { label: '删除', action: handleDelete }
    ];

    // 这里可以实现自定义右键菜单
    console.log('右键菜单:', menu);
  };

  return (
    <motion.div
      ref={clipRef}
      className={`clip-item ${clip.type}`}
      style={{ 
        backgroundColor: trackColor,
        minWidth: `${Math.max(clip.duration * 10, 80)}px` // 根据时长调整宽度
      }}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      draggable={true}
      onDragStart={handleDragStart}
      onDragEnd={endDragClip}
      whileHover={{ scale: 1.02 }}
    >
      {/* 修剪手柄 - 开始 */}
      <div 
        className="trim-handle trim-start"
        onMouseDown={handleTrimStart}
        title="拖拽修剪开始时间"
      />

      {/* 片段内容 */}
      <div className="clip-content">
        {/* 水印 */}
        {watermark && (
          <div className="clip-watermark">
            {watermark}
          </div>
        )}

        {/* 片段信息 */}
        <div className="clip-info">
          <div className="clip-title">
            {clip.title}
          </div>
          <div className="clip-duration">
            {formatClipTime(clip.duration)}
          </div>
        </div>

        {/* 编辑按钮 */}
        <div className="clip-controls">
          <button 
            className="btn-edit-clip"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            title="编辑片段"
          >
            ✏️
          </button>
        </div>
      </div>

      {/* 修剪手柄 - 结束 */}
      <div 
        className="trim-handle trim-end"
        onMouseDown={handleTrimEnd}
        title="拖拽修剪结束时间"
      />

      {/* 工具提示 */}
      {showTooltip && (
        <div className="clip-tooltip">
          <div><strong>{clip.title}</strong></div>
          <div>开始: {formatClipTime(clip.startTime)}</div>
          <div>结束: {formatClipTime(clip.endTime)}</div>
          <div>时长: {formatClipTime(clip.duration)}</div>
          {clip.description && (
            <div>描述: {clip.description}</div>
          )}
          <div className="tooltip-actions">
            <small>双击预览片段 | 右键菜单</small>
          </div>
        </div>
      )}

      {/* 编辑对话框 - 使用Portal渲染到document.body */}
      {isEditing && createPortal(
        <div 
          className="clip-edit-overlay"
          onClick={handleCancelEdit}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}
        >
          {/* 对话框内容 */}
          <div 
            className="clip-edit-dialog"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#2d2d2d',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '420px',
              maxWidth: '500px',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              color: 'white',
              border: '1px solid #444'
            }}
          >
            <div className="edit-dialog-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              borderBottom: '1px solid #444',
              paddingBottom: '12px'
            }}>
              <h4 style={{ 
                margin: 0, 
                color: trackColor, 
                fontSize: '18px',
                fontWeight: '600'
              }}>
                编辑片段
              </h4>
              <button 
                onClick={handleCancelEdit}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ccc',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#444'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>
            
            <div className="edit-dialog-content">
              <div className="edit-field" style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  color: '#ccc',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  标题:
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  maxLength={50}
                  placeholder="片段标题"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #555',
                    borderRadius: '6px',
                    backgroundColor: '#1a1a1a',
                    color: 'white',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = trackColor}
                  onBlur={(e) => e.target.style.borderColor = '#555'}
                  autoFocus
                />
              </div>
              
              <div className="edit-field" style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  color: '#ccc',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  描述:
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="片段描述"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #555',
                    borderRadius: '6px',
                    backgroundColor: '#1a1a1a',
                    color: 'white',
                    fontSize: '14px',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    minHeight: '80px'
                  }}
                  onFocus={(e) => e.target.style.borderColor = trackColor}
                  onBlur={(e) => e.target.style.borderColor = '#555'}
                />
              </div>

              <div className="edit-time-info" style={{
                backgroundColor: '#1a1a1a',
                padding: '16px',
                borderRadius: '6px',
                marginBottom: '24px',
                fontSize: '13px',
                color: '#ccc',
                border: '1px solid #333'
              }}>
                <div style={{ 
                  marginBottom: '8px',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span>开始时间:</span>
                  <span style={{ color: 'white', fontFamily: 'monospace', fontWeight: '500' }}>
                    {formatClipTime(clip.startTime)}
                  </span>
                </div>
                <div style={{ 
                  marginBottom: '8px',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span>结束时间:</span>
                  <span style={{ color: 'white', fontFamily: 'monospace', fontWeight: '500' }}>
                    {formatClipTime(clip.endTime)}
                  </span>
                </div>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span>片段时长:</span>
                  <span style={{ color: trackColor, fontFamily: 'monospace', fontWeight: '600' }}>
                    {formatClipTime(clip.duration)}
                  </span>
                </div>
              </div>
              
              <div className="edit-actions" style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}>
                <button 
                  onClick={handleCancelEdit} 
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #555',
                    borderRadius: '6px',
                    backgroundColor: 'transparent',
                    color: '#ccc',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#444';
                    e.target.style.borderColor = '#666';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#555';
                  }}
                >
                  取消
                </button>
                <button 
                  onClick={handleSaveEdit} 
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: trackColor,
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.filter = 'brightness(1.1)';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.filter = 'brightness(1)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </motion.div>
  );
};

export default ClipItem;